/** The catalyst inbox's pure parts: the lineage of a correction chain and the receipt lag (U051-U053). */
import type { CatalystRow } from "../../contracts/ops.ts";
import { lagUs } from "../ops-common.ts";

export interface Lineage {
  /** Earlier items this one corrects or updates, oldest first. */
  readonly predecessors: readonly CatalystRow[];
  /** Later items that correct or update this one, nearest first. */
  readonly successors: readonly CatalystRow[];
}

/** Follow `supersedes` back and forward within the loaded rows; a cycle or a missing link ends the chain. */
export function lineageOf(rows: readonly CatalystRow[], itemId: string): Lineage {
  const byId = new Map(rows.map((row) => [row.itemId, row]));
  const seen = new Set<string>([itemId]);
  const predecessors: CatalystRow[] = [];
  let at = byId.get(itemId)?.supersedes ?? null;
  while (at !== null && !seen.has(at)) {
    seen.add(at);
    const found = byId.get(at);
    if (found === undefined) {
      break;
    }
    predecessors.unshift(found);
    at = found.supersedes;
  }
  const successors: CatalystRow[] = [];
  let current = itemId;
  for (;;) {
    const next = rows.find((row) => row.supersedes === current && !seen.has(row.itemId));
    if (next === undefined) {
      break;
    }
    seen.add(next.itemId);
    successors.push(next);
    current = next.itemId;
  }
  return { predecessors, successors };
}

/** Publication-to-receipt lag in microseconds. */
export function receiptLagUs(row: CatalystRow): number {
  return lagUs(row.publishedAt, row.receivedAt);
}

/** Receipt-to-classification lag in microseconds (processing, distinct from publication). */
export function processingLagUs(row: CatalystRow): number {
  return lagUs(row.receivedAt, row.processedAt);
}
